/**
 * Product Stock Drawer Component
 * Shows detailed stock information for a single product
 * @see US-12b.5 - Drawer Detail Produit
 * @see Sprint 27 - Stock a Date (vue agregee)
 */

import { useCallback } from 'react';
import {
  TrendingUp,
  Calculator,
  ClipboardList,
} from 'lucide-react';
import { Drawer, Skeleton, isDateParamToday, formatDateParamDisplay } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes, formatQuantity } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useStockBreakdown } from '../hooks/useCurrentStock';
import type { MovementTypeAggregate } from '@bstock/shared';

interface ProductStockDrawerProps {
  rawProductId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showFinancials?: boolean;
  /** Date au format YYYY-MM-DD (Sprint 27) */
  date?: string | null;
}

/**
 * Order movements by type in the desired order
 */
const MOVEMENT_ORDER = ['RECEPTION', 'TRANSFER_IN', 'TRANSFER_OUT', 'LOSS', 'POS_SALE'];

function getOrderedMovements(movements: MovementTypeAggregate[]): MovementTypeAggregate[] {
  const movementMap = new Map(movements.map(m => [m.type, m]));
  const ordered: MovementTypeAggregate[] = [];

  // Add in specified order
  for (const type of MOVEMENT_ORDER) {
    const movement = movementMap.get(type);
    if (movement && movement.movementCount > 0) {
      ordered.push(movement);
    }
  }

  // Add any remaining types not in the order list
  for (const movement of movements) {
    if (!MOVEMENT_ORDER.includes(movement.type) && movement.movementCount > 0) {
      ordered.push(movement);
    }
  }

  return ordered;
}

/**
 * Movement Card Component
 */
function MovementCard({ movement, unitSymbol }: { movement: MovementTypeAggregate; unitSymbol: string }) {
  const isPositive = movement.netQuantity > 0;
  const isNegative = movement.netQuantity < 0;

  return (
    <div className="p-3 bg-white border border-neutral-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-dark-500">
            {movement.label}
          </p>
          <p className="text-xs text-neutral-500 mt-0.5">
            {movement.movementCount} mouvement{movement.movementCount > 1 ? 's' : ''}
          </p>
        </div>
        <div className="text-right">
          <span
            className={cn(
              'text-lg font-semibold',
              isPositive ? 'text-success-600' : isNegative ? 'text-danger-600' : 'text-neutral-400'
            )}
          >
            {isPositive ? '+' : ''}{formatQuantity(movement.netQuantity)}
          </span>
          <span className="text-xs text-neutral-400 ml-1">
            {unitSymbol}
          </span>
        </div>
      </div>
    </div>
  );
}

export function ProductStockDrawer({
  rawProductId,
  open,
  onOpenChange,
  showFinancials = false,
  date,
}: ProductStockDrawerProps) {
  const currency = useCurrency();
  const { formatDateTimeShort } = useFormatDate();

  // Use the breakdown endpoint (Sprint 27)
  const { data, isLoading } = useStockBreakdown(
    open ? rawProductId : null,
    { date: date || undefined }
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      onOpenChange(isOpen);
    },
    [onOpenChange]
  );

  // Format stock date title
  const stockDateTitle = data
    ? isDateParamToday(data.date)
      ? 'Stock actuel'
      : `Stock au ${formatDateParamDisplay(data.date)}`
    : 'Stock';

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      title={data?.rawProduct.name ?? 'Detail produit'}
      description={data ? `${data.rawProduct.sku} - ${stockDateTitle}` : undefined}
      size="large"
    >
      {isLoading || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-32" />
          <Skeleton className="h-48" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stock Summary */}
          <div className="p-4 bg-primary-50 rounded-lg border border-primary-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-primary-600 font-medium mb-1">
                  {stockDateTitle}
                </p>
                <div className="flex items-baseline gap-2">
                  <span
                    className={cn(
                      'text-3xl font-bold',
                      data.stockAtDate < 0 ? 'text-danger-600' : 'text-dark-500'
                    )}
                  >
                    {formatQuantity(data.stockAtDate)}
                  </span>
                  <span className="text-sm text-neutral-500">
                    {data.rawProduct.unitSymbol}
                  </span>
                </div>
              </div>
              {showFinancials && data.pmpCentimes !== null && (
                <div className="text-right">
                  <p className="text-xs text-neutral-500 mb-1">Valeur PMP</p>
                  <span className="text-xl font-semibold text-dark-500">
                    {formatCentimes(data.valueCentimes, currency)}
                  </span>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    ({formatCentimes(data.pmpCentimes, currency)}/{data.rawProduct.unitSymbol})
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Tare Section */}
          <div className="border-t border-neutral-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="w-4 h-4 text-primary-500" />
              <h4 className="text-sm font-medium text-dark-500">Tare (Inventaire de reference)</h4>
            </div>

            {data.tare ? (
              <div className="p-3 bg-white border border-neutral-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-dark-500">
                      {data.tare.inventoryNumber}
                    </p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      Date effective: {formatDateTimeShort(data.tare.effectiveDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-semibold text-dark-500">
                      {formatQuantity(data.tare.countedQty)}
                    </span>
                    <span className="text-xs text-neutral-400 ml-1">
                      {data.rawProduct.unitSymbol}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                <p className="text-sm text-neutral-500 text-center">
                  Aucun inventaire valide avant cette date
                </p>
                <p className="text-xs text-neutral-400 text-center mt-1">
                  Le calcul demarre depuis le debut de l'historique
                </p>
              </div>
            )}
          </div>

          {/* Movements by Type Section - Cards */}
          <div className="border-t border-neutral-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-primary-500" />
              <h4 className="text-sm font-medium text-dark-500">
                Mouvements depuis la tare
              </h4>
            </div>

            {data.movementsByType.length === 0 ? (
              <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                <p className="text-sm text-neutral-500 text-center">
                  Aucun mouvement sur cette periode
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Order: RECEPTION, TRANSFER_IN, TRANSFER_OUT, LOSS, POS_SALE */}
                {getOrderedMovements(data.movementsByType).map((agg: MovementTypeAggregate) => (
                  <MovementCard
                    key={agg.type}
                    movement={agg}
                    unitSymbol={data.rawProduct.unitSymbol}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Calculation Formula */}
          <div className="border-t border-neutral-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="w-4 h-4 text-primary-500" />
              <h4 className="text-sm font-medium text-dark-500">Calcul du stock</h4>
            </div>

            <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
              <div className="flex items-center justify-center gap-3 text-lg">
                <span className="font-medium text-dark-500">
                  Tare ({formatQuantity(data.calculation.tare)})
                </span>
                <span className="text-neutral-400">+</span>
                <span
                  className={cn(
                    'font-medium',
                    data.calculation.movements > 0
                      ? 'text-success-600'
                      : data.calculation.movements < 0
                      ? 'text-danger-600'
                      : 'text-neutral-500'
                  )}
                >
                  Mouvements ({data.calculation.movements > 0 ? '+' : ''}
                  {formatQuantity(data.calculation.movements)})
                </span>
                <span className="text-neutral-400">=</span>
                <span
                  className={cn(
                    'font-bold text-xl',
                    data.calculation.result < 0 ? 'text-danger-600' : 'text-primary-600'
                  )}
                >
                  {formatQuantity(data.calculation.result)}
                </span>
                <span className="text-sm text-neutral-500">{data.rawProduct.unitSymbol}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
